export interface ServiceSpec {
    label: string
    value: string
}

export interface Service {
    id: string
    number: string
    title: string
    summary: string
    body: string
    specs: Array<ServiceSpec>
    visual: { kind: 'cloud', shape: string, label: string } | { kind: 'sketch', sketch: 'retrofit' | 'massing', label: string }
    examples: Array<{ label: string, href: string }>
}

export const SERVICES: Array<Service> = [
    {
        id: 'new-architecture',
        number: '01',
        title: 'New architecture',
        summary: 'Civic, cultural and housing buildings from concept to completion.',
        body: 'Every new building starts from a scanned site and is designed in the same model that will later monitor it. One partner leads the whole team from the first sketch to the keys, and the studio stays for the first ten years of use.',
        specs: [
            { label: 'Typical size', value: '2 000 to 15 000 m²' },
            { label: 'Structure', value: 'Timber first' },
            { label: 'Team', value: '6 to 14 people' }
        ],
        visual: { kind: 'cloud', shape: 'library', label: 'Point cloud of Hallmark Library, a three-storey timber library with a stepped roof.' },
        examples: [
            { label: 'Hallmark Library', href: '/projects/hallmark-library' },
            { label: 'Ostra Cultural Hall', href: '/projects/ostra-cultural-hall' }
        ]
    },
    {
        id: 'retrofit-and-heritage',
        number: '02',
        title: 'Retrofit and heritage',
        summary: 'Surveying, repairing and reusing existing buildings.',
        body: 'A building that is already standing holds most of its carbon. We scan it to 2 mm, find what is sound, and design the repairs and the new use around the structure that is already there, so the cheapest tonne of carbon stays in the walls.',
        specs: [
            { label: 'Survey accuracy', value: '2 mm' },
            { label: 'Carbon saved vs new build', value: '55 to 70%' },
            { label: 'Delivery', value: '8 to 24 months' }
        ],
        visual: { kind: 'sketch', sketch: 'retrofit', label: 'Line drawing of an existing gabled facade with survey points and marked repairs.' },
        examples: [
            { label: 'Our Stockholm office, a 1911 print works', href: '/studio' }
        ]
    },
    {
        id: 'survey-and-digital-twin',
        number: '03',
        title: 'Survey and digital twin',
        summary: 'Scanning any building and handing over a living model.',
        body: 'A two-person crew captures the building in a day with a robotic total station and drone lidar. Within 48 hours you receive the registered point cloud, a clean model built from it and a report of every place the building differs from its drawings.',
        specs: [
            { label: 'Scan density', value: '2 mm at 10 m' },
            { label: 'Delivery', value: '48 h' },
            { label: 'Output', value: 'Point cloud, model, deviation report' }
        ],
        visual: { kind: 'cloud', shape: 'office-sto', label: 'Point cloud of a five-storey brick building with a gable roof, scanned as found.' },
        examples: [
            { label: 'The platform: capture', href: '/platform#capture' }
        ]
    },
    {
        id: 'building-performance',
        number: '04',
        title: 'Building performance',
        summary: 'Sensors, dashboards and annual audits on finished buildings.',
        body: 'We fit sensors for heat, air, light and movement, compare every reading with the design model and send a short report each month. At the end of each year an audit sets out what to tune, repair or leave alone.',
        specs: [
            { label: 'Sensors per 1 000 m²', value: '38' },
            { label: 'Sample interval', value: '60 s' },
            { label: 'Report', value: 'Monthly' }
        ],
        visual: { kind: 'cloud', shape: 'housing', label: 'Thermal model of Casa Mares, its facade coloured by heat loss from cool grey to blue.' },
        examples: [
            { label: 'Casa Mares', href: '/projects/casa-mares' },
            { label: 'The live model', href: '/live-model' }
        ]
    },
    {
        id: 'competitions-and-feasibility',
        number: '05',
        title: 'Competitions and feasibility',
        summary: 'Fast studies for clients deciding whether and how to build.',
        body: 'In three weeks we test the site, compare massing options and set a carbon budget for each, with a cost range you can take to a board. Many studies end in a smaller building, a retrofit or a decision not to build at all.',
        specs: [
            { label: 'Turnaround', value: '3 weeks' },
            { label: 'Deliverable', value: 'Massing, carbon budget, cost range' }
        ],
        visual: { kind: 'sketch', sketch: 'massing', label: 'Three massing options drawn as line boxes, each tagged with its carbon budget.' },
        examples: [
            { label: 'How a commission runs', href: '/process' }
        ]
    }
]

export interface Partnership {
    name: string
    note: string
}

export const CLIENT_TYPES: Array<Partnership> = [
    { name: 'Municipalities', note: 'Libraries, schools and civic buildings that must last a century.' },
    { name: 'Cultural institutions', note: 'Halls and museums where crowds, light and air change by the hour.' },
    { name: 'Housing cooperatives', note: 'Homes owned by the people who live in them and pay the bills.' },
    { name: 'Universities', note: 'Teaching and research buildings used as living laboratories.' },
    { name: 'Hospital estates', note: 'Wards and plant rooms surveyed and monitored while in use.' }
]
